/**
 * Unify QuestionnaireResponse model — comprehensive FHIR schema.
 *
 * QuestionnaireResponses represent completed/in-progress clinical notes,
 * scored assessments, intake documentation, and supervision cosign items.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────┐
 * │ Field             │ Type                │ Description                         │
 * ├──────────────────────────────────────────────────────────────────────────────┤
 * │ questionnaire     │ canonical(Questionnaire) │ Reference to template         │
 * │ subject           │ Reference<Patient>  │ Patient this note is about          │
 * │ author            │ Reference<Practitioner> │ Clinician who authored          │
 * │ encounter         │ Reference<Encounter>│ Optional encounter context          │
 * │ source            │ Reference           │ Who provided the answers            │
 * │ status            │ code                │ in-progress|completed|amended|stopped│
 * │ authored          │ dateTime            │ When the response was authored      │
 * │ item[]            │ Item[]              │ Answers to questionnaire items      │
 * │ meta.tag[]        │ Coding[]            │ Appointment link, template category │
 * │ extension[]       │ Extension[]         │ Signature, scoring, supervision     │
 * └──────────────────────────────────────────────────────────────────────────────┘
 *
 * meta.tag systems:
 *  - APPOINTMENT_TAG_SYSTEM     → links QR to an Appointment (code = appointment id)
 *  - TEMPLATE_CATEGORY_SYSTEM   → note template type (appointments, measures, etc.)
 *  - REQUESTED_EDITS_TAG_SYSTEM → "true" when supervisor has requested edits
 *  - UNIFY_TAG_SYSTEM / exemplar-note → marks QR as an AI exemplar for its template
 *
 * Custom extensions:
 *  - DETAILED_STATUS_EXT          → valueCode: fine-grained workflow status
 *  - PROVIDER_SIGNATURE_EXT_URL   → valueString: JSON {name, license, signedAt, requestSignedAt?, signatureMode, typedText}
 *  - SUPERVISOR_SIGNATURE_EXT_URL → valueString: JSON supervisor e-signature
 *  - REQUESTED_EDITS_EXT_URL      → valueString: "true" or edit request text
 *  - REVIEW_COMMENTS_EXT_URL      → valueString: JSON array of review comments
 *  - QR_TOTAL_SCORE_EXT           → valueInteger: computed assessment total score
 *  - QR_INTERPRETATION_EXT        → valueString: clinical interpretation of score
 *  - SIGNATURE_IP_EXT             → valueString: IP address at time of signing
 *  - SIGNATURE_TYPED_TEXT_EXT     → valueString: typed-name e-signature text
 *  - EPISODE_OF_CARE_CONTEXT_EXT  → valueReference: EpisodeOfCare context link
 */

import type { QuestionnaireResponse, Extension, Reference } from '@medplum/fhirtypes';

import {
  DETAILED_STATUS_EXT,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
  REQUESTED_EDITS_EXT_URL,
  REVIEW_COMMENTS_EXT_URL,
  QR_TOTAL_SCORE_EXT,
  QR_INTERPRETATION_EXT,
  SIGNATURE_IP_EXT,
  SIGNATURE_TYPED_TEXT_EXT,
  APPOINTMENT_TAG_SYSTEM,
  TEMPLATE_CATEGORY_SYSTEM,
  EPISODE_OF_CARE_CONTEXT_EXT,
  EXEMPLAR_NOTE_TAG,
  PORTAL_TASK_REFERENCE_EXT,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
//  Coded value types
// ═══════════════════════════════════════════════════════════════════════════

/** QuestionnaireResponse.status values used by Unify. */
export type QRStatus = 'in-progress' | 'completed' | 'amended' | 'stopped';

/** Detailed status codes for the DETAILED_STATUS_EXT extension. */
export type DetailedStatusCode =
  | 'draft'
  | 'pending-signature'
  | 'signed'
  | 'pending-cosign'
  | 'cosigned'
  | 'needs-edits'
  | 'amended';

/**
 * Shape of the provider/supervisor signature JSON stored in valueString.
 * A server-built provider signature (`signatureMode: 'server'`) carries the server-clock `signedAt` and
 * `requestSignedAt`, the request time the signer submitted. Only stored/history reads use `requestSignedAt`.
 */
export interface SignatureInfo {
  name: string;
  license?: string;
  signedAt: string;
  /** Request time the signer submitted (server-built signatures only); `signedAt` is then the server clock. */
  requestSignedAt?: string;
  signatureMode?: 'type' | 'draw' | 'server';
  typedText?: string;
  ipAddress?: string;
}

/** Shape of a single review comment stored in REVIEW_COMMENTS_EXT_URL JSON array. */
export interface ReviewComment {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: 'supervisor' | 'provider';
  text: string;
  createdAt: string;
  anchor?: { linkId: string; snippet?: string };
}

// ═══════════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Get the fine-grained workflow status (DETAILED_STATUS_EXT). */
export function getDetailedStatus(resource: QuestionnaireResponse): DetailedStatusCode | undefined {
  return resource.extension?.find((e) => e.url === DETAILED_STATUS_EXT)?.valueCode as DetailedStatusCode | undefined;
}

/** Get the parsed provider signature object, or undefined. */
export function getProviderSignature(resource: QuestionnaireResponse): SignatureInfo | undefined {
  const raw = resource.extension?.find((e) => e.url === PROVIDER_SIGNATURE_EXT_URL)?.valueString;
  return raw ? JSON.parse(raw) : undefined;
}

/** Get the parsed supervisor signature object, or undefined. */
export function getSupervisorSignature(resource: QuestionnaireResponse): SignatureInfo | undefined {
  const raw = resource.extension?.find((e) => e.url === SUPERVISOR_SIGNATURE_EXT_URL)?.valueString;
  return raw ? JSON.parse(raw) : undefined;
}

/** Get the requested edits text (or "true" flag). */
export function getRequestedEdits(resource: QuestionnaireResponse): string | undefined {
  return resource.extension?.find((e) => e.url === REQUESTED_EDITS_EXT_URL)?.valueString;
}

/** Get the parsed review comments array, or undefined. */
export function getReviewComments(resource: QuestionnaireResponse): ReviewComment[] | undefined {
  const raw = resource.extension?.find((e) => e.url === REVIEW_COMMENTS_EXT_URL)?.valueString;
  return raw ? JSON.parse(raw) : undefined;
}

/** Get the computed total score (for scored measures like PHQ-9, GAD-7). */
export function getTotalScore(resource: QuestionnaireResponse): number | undefined {
  return resource.extension?.find((e) => e.url === QR_TOTAL_SCORE_EXT)?.valueInteger;
}

/** Get the clinical interpretation string for the score. */
export function getInterpretation(resource: QuestionnaireResponse): string | undefined {
  return resource.extension?.find((e) => e.url === QR_INTERPRETATION_EXT)?.valueString;
}

/** Get the IP address recorded at time of signing. */
export function getSignatureIp(resource: QuestionnaireResponse): string | undefined {
  return resource.extension?.find((e) => e.url === SIGNATURE_IP_EXT)?.valueString;
}

/** Get the typed-name text used for e-signature. */
export function getSignatureTypedText(resource: QuestionnaireResponse): string | undefined {
  return resource.extension?.find((e) => e.url === SIGNATURE_TYPED_TEXT_EXT)?.valueString;
}

/** Get the linked Appointment id from meta.tag (APPOINTMENT_TAG_SYSTEM). */
export function getAppointmentId(resource: QuestionnaireResponse): string | undefined {
  return resource.meta?.tag?.find((t) => t.system === APPOINTMENT_TAG_SYSTEM)?.code;
}

/** Get the template category from meta.tag (TEMPLATE_CATEGORY_SYSTEM). */
export function getQRTemplateCategory(resource: QuestionnaireResponse): string | undefined {
  return resource.meta?.tag?.find((t) => t.system === TEMPLATE_CATEGORY_SYSTEM)?.code;
}

/** True when this QR is tagged as an exemplar note for AI generation. */
export function isExemplarNote(resource: QuestionnaireResponse): boolean {
  return (
    resource.meta?.tag?.some(
      (t) => t.system === EXEMPLAR_NOTE_TAG.system && t.code === EXEMPLAR_NOTE_TAG.code,
    ) ?? false
  );
}

/** Get the EpisodeOfCare context reference. */
export function getEpisodeOfCareContext(resource: QuestionnaireResponse): Reference | undefined {
  return resource.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)?.valueReference;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Setter helpers
// ═══════════════════════════════════════════════════════════════════════════

function setExtension(resource: QuestionnaireResponse, ext: Extension): void {
  if (!resource.extension) resource.extension = [];
  const idx = resource.extension.findIndex((e) => e.url === ext.url);
  if (idx >= 0) {
    resource.extension[idx] = ext;
  } else {
    resource.extension.push(ext);
  }
}

/** Set the fine-grained workflow status. */
export function setDetailedStatus(resource: QuestionnaireResponse, code: DetailedStatusCode): void {
  setExtension(resource, { url: DETAILED_STATUS_EXT, valueCode: code });
}

/** Set the provider signature (serializes to JSON valueString). */
export function setProviderSignature(resource: QuestionnaireResponse, sig: SignatureInfo): void {
  setExtension(resource, { url: PROVIDER_SIGNATURE_EXT_URL, valueString: JSON.stringify(sig) });
}

/** Set the supervisor signature (serializes to JSON valueString). */
export function setSupervisorSignature(resource: QuestionnaireResponse, sig: SignatureInfo): void {
  setExtension(resource, { url: SUPERVISOR_SIGNATURE_EXT_URL, valueString: JSON.stringify(sig) });
}

/** Set the requested edits flag/text. */
export function setRequestedEdits(resource: QuestionnaireResponse, value: string): void {
  setExtension(resource, { url: REQUESTED_EDITS_EXT_URL, valueString: value });
}

/** Set the review comments (serializes array to JSON valueString). */
export function setReviewComments(resource: QuestionnaireResponse, comments: ReviewComment[]): void {
  setExtension(resource, { url: REVIEW_COMMENTS_EXT_URL, valueString: JSON.stringify(comments) });
}

/** Set the computed total score. */
export function setTotalScore(resource: QuestionnaireResponse, score: number): void {
  setExtension(resource, { url: QR_TOTAL_SCORE_EXT, valueInteger: score });
}

/** Set the clinical interpretation string. */
export function setInterpretation(resource: QuestionnaireResponse, interpretation: string): void {
  setExtension(resource, { url: QR_INTERPRETATION_EXT, valueString: interpretation });
}

/** Set the signature IP address. */
export function setSignatureIp(resource: QuestionnaireResponse, ip: string): void {
  setExtension(resource, { url: SIGNATURE_IP_EXT, valueString: ip });
}

/** Set the typed-name e-signature text. */
export function setSignatureTypedText(resource: QuestionnaireResponse, text: string): void {
  setExtension(resource, { url: SIGNATURE_TYPED_TEXT_EXT, valueString: text });
}

/** Set or replace the appointment id tag. */
export function setAppointmentId(resource: QuestionnaireResponse, appointmentId: string): void {
  if (!resource.meta) resource.meta = {};
  if (!resource.meta.tag) resource.meta.tag = [];
  const existing = resource.meta.tag.find((t) => t.system === APPOINTMENT_TAG_SYSTEM);
  if (existing) {
    existing.code = appointmentId;
  } else {
    resource.meta.tag.push({ system: APPOINTMENT_TAG_SYSTEM, code: appointmentId });
  }
}

/** Set or replace the template category tag. */
export function setQRTemplateCategory(resource: QuestionnaireResponse, category: string): void {
  if (!resource.meta) resource.meta = {};
  if (!resource.meta.tag) resource.meta.tag = [];
  const existing = resource.meta.tag.find((t) => t.system === TEMPLATE_CATEGORY_SYSTEM);
  if (existing) {
    existing.code = category;
  } else {
    resource.meta.tag.push({ system: TEMPLATE_CATEGORY_SYSTEM, code: category });
  }
}

/** Add or remove the exemplar-note meta.tag. */
export function setExemplarNoteTag(resource: QuestionnaireResponse, isExemplar: boolean): void {
  if (!resource.meta) resource.meta = {};
  const otherTags = (resource.meta.tag ?? []).filter(
    (t) => t.system !== EXEMPLAR_NOTE_TAG.system || t.code !== EXEMPLAR_NOTE_TAG.code,
  );
  resource.meta.tag = isExemplar ? [...otherTags, EXEMPLAR_NOTE_TAG] : otherTags;
}

/** Set the EpisodeOfCare context reference. */
export function setEpisodeOfCareContext(resource: QuestionnaireResponse, ref: Reference): void {
  setExtension(resource, { url: EPISODE_OF_CARE_CONTEXT_EXT, valueReference: ref });
}

export function getPortalTaskReference(resource: QuestionnaireResponse): Reference | undefined {
  return resource.extension?.find((e) => e.url === PORTAL_TASK_REFERENCE_EXT)?.valueReference;
}

export function setPortalTaskReference(resource: QuestionnaireResponse, ref: Reference): void {
  setExtension(resource, { url: PORTAL_TASK_REFERENCE_EXT, valueReference: ref });
}

/** Portal extension first; legacy QR.basedOn → Task kept for read fallback only. */
export function getLinkedTaskReference(resource: QuestionnaireResponse): Reference | undefined {
  return (
    getPortalTaskReference(resource) ??
    resource.basedOn?.find((b) => b.reference?.startsWith('Task/'))
  );
}

export function getLinkedTaskId(resource: QuestionnaireResponse): string | undefined {
  const ref = getLinkedTaskReference(resource)?.reference;
  return ref?.startsWith('Task/') ? ref.slice('Task/'.length) : undefined;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Signed-note lock
// ═══════════════════════════════════════════════════════════════════════════

/**
 * True when the note carries a signature, which freezes the linked appointment.
 * `needs-edits` is excluded: a supervisor requesting edits clears the signature,
 * so the session is editable again.
 */
export function isClinicalNoteLockedStatus(status: string | undefined): boolean {
  return status === 'signed' || status === 'cosigned';
}

/** The note fields the appointment lock depends on. */
export interface ClinicalNoteLockSlice {
  detailedStatus?: string;
  /** `QuestionnaireResponse.author.reference`, e.g. `Practitioner/abc`. */
  authorReference?: string;
}

export function clinicalNoteLockSlice(resource: QuestionnaireResponse): ClinicalNoteLockSlice {
  return {
    detailedStatus: getDetailedStatus(resource),
    authorReference: resource.author?.reference,
  };
}

/**
 * True when a signed note blocks schedule edits on its appointment.
 *
 * The signing provider keeps write access so the Revise Note flow can correct the
 * session alongside the addendum; everyone else is blocked until a supervisor
 * requests edits (which drops the note out of the locked statuses).
 */
export function signedNoteBlocksAppointmentEdit(
  notes: readonly ClinicalNoteLockSlice[],
  actorPractitionerId: string | undefined,
): boolean {
  const locked = notes.filter((note) => isClinicalNoteLockedStatus(note.detailedStatus));
  if (locked.length === 0) return false;
  if (!actorPractitionerId) return true;
  const actorRef = `Practitioner/${actorPractitionerId}`;
  return !locked.every((note) => note.authorReference === actorRef);
}
